import { useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Navigate } from 'react-router-dom';

import { So5LeaderboardType } from '@sorare/core/src/__generated__/globalTypes';
import { Img } from '@sorare/core/src/atoms/ui/Img';
import { League } from '@sorare/core/src/components/landing/types';
import { FOOTBALL_PLAY } from '@sorare/core/src/constants/__generated__/routes';
import { useSeoContext } from '@sorare/core/src/contexts/seo';
import { useIsLoggedIn } from '@sorare/core/src/hooks/auth/useIsLoggedIn';

import { WalletLanding } from 'components/landing/WalletLanding';
import { winAssets } from 'pages/assets/assets';

import heroBgAvif from './assets/hero/bg.avif';
import heroBg from './assets/hero/bg.webp';
import heroPlayersLeftSideAvif from './assets/hero/players/leftSidePlayers.avif';
import heroPlayersLeftSide from './assets/hero/players/leftSidePlayers.webp';
import heroPlayersRightSideAvif from './assets/hero/players/rightSidePlayers.avif';
import heroPlayersRightSide from './assets/hero/players/rightSidePlayers.webp';
import leagueLogo from './assets/league-logo.webp';
import playFieldAvif from './assets/play/field.avif';
import playField from './assets/play/field.webp';
import lineupAvif from './assets/play/lineup.avif';
import lineup from './assets/play/lineup.webp';
import scoutBgAvif from './assets/scout/background.avif';
import scoutBg from './assets/scout/background.webp';
import scoutPlayersAvif from './assets/scout/players.avif';
import scoutPlayers from './assets/scout/players.webp';
import social from './assets/social.webp';

const messages = defineMessages({
  metadatasTitle: {
    id: 'BundesligaLanding.metadatas.title',
    defaultMessage: 'The Real Fantasy Bundesliga Game – Sorare Football',
  },
  metadatasDescription: {
    id: 'BundesligaLanding.metadatas.description',
    defaultMessage:
      'Bundesliga: Play Sorare’s free fantasy Bundesliga game. Collect, buy, sell, and compete with ownable digital player cards to win rewards.',
  },
});

const heroAssets = {
  background: {
    avif: heroBgAvif,
    pictureUrl: heroBg,
  },
  players: [
    {
      avif: heroPlayersLeftSideAvif,
      pictureUrl: heroPlayersLeftSide,
      height: 575,
      width: 564,
    },
    {
      avif: heroPlayersRightSideAvif,
      pictureUrl: heroPlayersRightSide,
      height: 563,
      width: 484,
    },
  ],
};

const scoutAssets = {
  background: {
    avif: scoutBgAvif,
    pictureUrl: scoutBg,
  },
  players: {
    avif: scoutPlayersAvif,
    pictureUrl: scoutPlayers,
  },
};

const playAssets = {
  background: {
    avif: playFieldAvif,
    pictureUrl: playField,
  },
  lineup: {
    avif: lineupAvif,
    pictureUrl: lineup,
  },
};

const FootballPage = () => {
  const isLoggedIn = useIsLoggedIn();
  const { formatMessage } = useIntl();
  const { setPageMetadata } = useSeoContext();

  useEffect(
    () =>
      setPageMetadata(formatMessage(messages.metadatasTitle), {
        img: social,
        description: formatMessage(messages.metadatasDescription),
      }),
    [setPageMetadata, formatMessage]
  );

  if (isLoggedIn) {
    return <Navigate to={FOOTBALL_PLAY} replace />;
  }

  return (
    <WalletLanding
      assets={{
        heroAssets,
        scoutAssets,
        playAssets,
        winAssets,
      }}
      leaderboards={[
        So5LeaderboardType.IN_SEASON_GERMANY_LIMITED,
        So5LeaderboardType.IN_SEASON_GERMANY_UNIQUE,
        So5LeaderboardType.IN_SEASON_GERMANY_RARE,
      ]}
      leagueLogo={
        <Img src={leagueLogo} alt="Bundesliga" width="240" height="80" />
      }
      league={League.Bundesliga}
    />
  );
};

export default FootballPage;
